'use client';
import { useSession } from 'next-auth/react';
import '@/globals.css';
import { useState, useEffect, useRef } from 'react';
import { saveSession, isIpInTrustedRange, createSessionsTable } from '@/actions/server/auth/sessions';
import { usePathname } from 'next/navigation';
import LocationSharingRequired from '@/components/LocationSharingRequired';
import { Loader } from 'lucide-react';
import axios from 'axios';
import smartlookClient from 'smartlook-client';
import { setPermissionsCache } from '@/utils/permissions';
import { loadAllPermissions as serverLoadAllPermissions } from '@/actions/server/permissions';

// Feature flag for mandatory location sharing - keep in sync with remote-logins page and sessions.js
const MANDATORY_LOCATION_SHARING = false; // Set to true to enforce location sharing

// Get client IP address - now returns null if there's any error
const getClientIP = async () => {
  try {
    const response = await axios.get('https://api.ipify.org?format=json');
    return response.data.ip;
  } catch (error) {
    console.warn('Failed to get IP address:', error);
    return null;
  }
};

// Request location with custom message and improved error handling
const requestLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    const handleSuccess = (position) => {
      resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    };

    const handleError = (error) => {
      console.warn('Geolocation error:', error.code, error.message);
      reject(error);
    };

    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  });
};

// Check if location permission is granted
const checkLocationPermission = async () => {
  if (!navigator.permissions || !navigator.permissions.query) {
    // Fallback for browsers without permissions API
    try {
      // Try to get location as a test
      await requestLocation();
      return 'granted';
    } catch (error) {
      return error.code === 1 ? 'denied' : 'prompt';
    }
  }
  
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch (error) {
    console.warn('Permission check error:', error);
    return 'unknown';
  }
};

const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen bg-white">
    <div className="flex flex-col items-center">
      <Loader className="w-8 h-8 animate-spin text-blue-500" />
      <span className="mt-4 text-gray-600">Loading...</span>
    </div>
  </div>
);

// Check if current path is a public route
const isPublicRoute = (pathname) => {
  return (
    pathname === '/login' || 
    pathname === '/forgot-password' || 
    pathname === '/forgot-password2' || 
    pathname === '/register' || 
    // pathname === '/register2' ||
    pathname === '/register/thankyou' ||
    pathname === '/register/resend-confirmation' ||
    pathname === '/register/confirm' ||
    pathname.startsWith('/reset-password2')
  );
};

// Function to load all permissions using server action
const refreshPermissionsCache = async () => {
  try {
    console.log('🔄 Refreshing permissions cache...');
    const allPermissions = await serverLoadAllPermissions();
    setPermissionsCache(allPermissions);
    console.log(`✅ Permissions cache refreshed with ${allPermissions.length} permissions`);
    return allPermissions;
  } catch (error) {
    console.error('❌ Failed to refresh permissions cache:', error);
    return [];
  }
};

export default function Template({ children }) {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const initializationComplete = useRef(false);
  const sessionChecked = useRef(false);
  const permissionsCacheInitialized = useRef(false);
  const lastPathname = useRef(pathname);
  const wasTabHidden = useRef(false);

  const [ state, setState ] = useState({
    isClient: false,
    isLoading: true,
    locationRequired: false,
    showResetInstructions: false
  });

  // Add visibility change detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        wasTabHidden.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Separate useEffect for permissions cache initialization
  useEffect(() => {
    // Skip refresh if this is just tab focus
    if (wasTabHidden.current && !document.hidden) {
      console.log('⏭️ Skipping permissions refresh on tab focus');
      wasTabHidden.current = false;
      return;
    }

    // Only refresh permissions when first loading the app or on actual navigation
    // Do not refresh on tab focus
    if (!permissionsCacheInitialized.current) {
      console.log('📍 Initial permissions load');
      refreshPermissionsCache();
      permissionsCacheInitialized.current = true;
      lastPathname.current = pathname;
    } else if (pathname !== lastPathname.current) {
      // Only refresh on actual navigation to a new path
      console.log('📍 Pathname changed, refreshing permissions');
      refreshPermissionsCache();
      lastPathname.current = pathname;
    }
  }, [pathname]);

  const updateState = newState => 
    setState(
        prevState => ({
            ...prevState,
            ...newState
        })
    );

  const handleRetryLocation = async () => {
    updateState({ isLoading: true, locationRequired: false });
    try {
      const clientIP = await getClientIP();
      const isOfficeIp = clientIP === '43.243.142.74';
      
      if (!isOfficeIp) {
        // Check permission status first
        const permissionStatus = await checkLocationPermission();
        
        if (permissionStatus === 'denied') {
          updateState({ 
            locationRequired: true, 
            isLoading: false,
            showResetInstructions: true 
          });
          return;
        }
        
        const browserLocation = await requestLocation();
        const user = session?.user;
        
        if (user?.email && clientIP) {
          const result = await saveSession(user.email, clientIP, browserLocation, user.id_role);
          if (result.success) {
            updateState({ isClient: true, isLoading: false });
          } else if (result.requireLocation) {
            updateState({ locationRequired: true, isLoading: false });
          }
        }
      } else {
        // Office IP, no need for location
        updateState({ isClient: true, isLoading: false });
      }
    } catch (error) {
      console.error('Location retry error:', error);
      if (error.code === 1) { // Permission denied again
        updateState({ 
          locationRequired: true, 
          isLoading: false,
          showResetInstructions: true 
        });
      } else {
        // Other errors, still allow access
        updateState({ isClient: true, isLoading: false });
      }
    }
  };

  // Handle public routes immediately
  const currentPathIsPublic = isPublicRoute(pathname);

  useEffect(() => {
    // For public routes, just mark as ready
    if (currentPathIsPublic) {
      updateState({ isClient: true, isLoading: false });
      return;
    }

    // For protected routes, check session
    if (status === 'loading') {
      return; // Wait for session to load
    }

    // Mark that we've checked the session to avoid duplicate checks
    if (sessionChecked.current) {
      return;
    }
    sessionChecked.current = true;

    // If unauthenticated, mark as not loading
    if (status === 'unauthenticated') {
      updateState({ isLoading: false });
      return;
    }

    // Only run initialization once when session is available
    if (status === 'authenticated' && !initializationComplete.current) {
      (async function() {   
        try {
          updateState({ isLoading: true });
          
          // Ensure sessions table exists
          await createSessionsTable().catch(console.error);
          
          const user = session?.user;
          
          // Initialize Smartlook if in production
          if (process.env.NEXT_PUBLIC_APP_ENV === 'production' && user?.id) {
            smartlookClient.init('7aa153134b7be861b13e0fe950206669917c9056', { region: 'eu' });
            smartlookClient.identify(user.id, {
              email: user.email
            });
          }
          
          if (user?.email) {
            const clientIP = await getClientIP();
            
            let ipInTrustedRange = true;
            if (clientIP) {
              try {
                ipInTrustedRange = isIpInTrustedRange(clientIP);
              } catch (error) {
                console.warn('IP validation error:', error);
              }
            }

            const isOfficeIp = clientIP === '43.243.142.74';
            let browserLocation = null;
            
            // Only request location for non-partner roles and non-office IPs
            if (clientIP && !isOfficeIp && user.id_role !== 4) {
              // Check permission status first
              const permissionStatus = await checkLocationPermission();
              
              if (permissionStatus === 'granted' || permissionStatus === 'prompt') {
                try {
                  browserLocation = await requestLocation();
                } catch (error) {
                  if (error.code === 1) { // Permission denied
                    updateState({
                      locationRequired: true,
                      isLoading: false,
                      showResetInstructions: permissionStatus === 'denied'
                    });
                    return;
                  } else {
                    // For other errors (timeout, position unavailable), 
                    // we'll continue without location data
                    console.warn('Non-critical location error:', error);
                  }
                }
              } else if (permissionStatus === 'denied' && MANDATORY_LOCATION_SHARING) {
                updateState({
                  locationRequired: true,
                  isLoading: false,
                  showResetInstructions: true
                });
                return;
              }
            }

            if (clientIP) {
              const result = await saveSession(user.email, clientIP, browserLocation, user.id_role);
              
              // Only block if location is required by server AND we're enforcing it
              if (!result.success && result.requireLocation && MANDATORY_LOCATION_SHARING) {
                updateState({ locationRequired: true, isLoading: false });
                return;
              }
            }
          }

          updateState({
            isClient: true,
            isLoading: false
          });
          
          initializationComplete.current = true;
        } catch (error) {
          console.error('Template error:', error);
          updateState({
            isClient: true,
            isLoading: false
          });
          
          initializationComplete.current = true;
        }
      })();
    }
  }, [pathname, status, session]);

  const handleForceLocationPrompt = () => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          if (result.state === 'denied') {
            // If permission is denied, show instructions to reset permissions
            updateState({
              showResetInstructions: true
            });
          } else {
            // Try requesting location again
            handleRetryLocation();
          }
        })
        .catch(error => {
          console.warn('Permission query error:', error);
          // Fallback to direct retry
          handleRetryLocation();
        });
    } else {
      // Fallback for browsers that don't support permissions API
      handleRetryLocation();
    }
  };

  // For public routes, show content immediately after initial loading
  if (currentPathIsPublic) {
    if (status === 'loading') {
      return <LoadingSpinner />;
    }
    return children;
  }

  // For protected routes
  if (status === 'loading') {
    return <LoadingSpinner />;
  }

  if (status === 'unauthenticated') {
    return null;
  }

  if (state.locationRequired) {
    return <LocationSharingRequired state={state} handleForceLocationPrompt={handleForceLocationPrompt} />;
  }

  // Only show loading spinner during initial client-side setup
  if (state.isLoading && !state.isClient) {
    return <LoadingSpinner />;
  }

  return children;
}
